import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface FieldType {
  type:
    | "text"
    | "number"
    | "date"
    | "money"
    | "area"
    | "percentage"
    | "boolean"
    | "email"
    | "phone";
  format?: string;
  unit?: string;
  description?: string;
}

export interface FieldConfig {
  fieldPath: string;
  displayName: string;
  fieldType: FieldType;
  isActive: boolean;
  priority: number; // Pour l'ordre d'affichage
}

export interface FieldConfigState {
  fieldConfigs: FieldConfig[];
  isInitialized: boolean;

  // Actions
  setFieldConfigs: (configs: FieldConfig[]) => void;
  updateFieldConfig: (fieldPath: string, updates: Partial<FieldConfig>) => void;
  addFieldConfig: (config: FieldConfig) => void;
  removeFieldConfig: (fieldPath: string) => void;
  resetToDefaults: () => void;
  analyzeAndInitializeFields: (data: any[]) => void;
  exportConfiguration: () => string;
  importConfiguration: (configData: string) => boolean;
}

/**
 * Analyse les données pour détecter automatiquement les types de champs
 */
function analyzeFieldType(fieldPath: string, values: any[]): FieldType {
  const cleanValues = values.filter((v) => v != null && v !== "").slice(0, 100); // Échantillon

  if (cleanValues.length === 0) {
    return { type: "text", description: "Unknown field type" };
  }

  // Analyser les valeurs pour détecter le type
  const sampleValues = cleanValues.slice(0, 10);

  // Détecter les dates
  const dateRegexes = [
    /^\d{4}-\d{1,2}-\d{1,2}$/, // YYYY-M-D or YYYY-MM-DD
    /^\d{1,2}\/\d{1,2}\/\d{4}$/, // M/D/YYYY or MM/DD/YYYY
    /^\d{1,2}\/\d{1,2}\/\d{2}$/, // M/D/YY or MM/DD/YY
    /^\d{4}-\d{1,2}-\d{1,2}T\d{1,2}:\d{1,2}:\d{1,2}/, // ISO datetime
  ];

  if (
    sampleValues.some(
      (valueArray) =>
        Array.isArray(valueArray) &&
        valueArray.length > 0 &&
        typeof valueArray[0] === "string" &&
        dateRegexes.some((regex) => regex.test(valueArray[0].trim()))
    )
  ) {
    return {
      type: "date",
      format: "MM/DD/YYYY",
      description: "Auto-detected date field",
    };
  }

  // Détecter l'argent USD
  if (
    sampleValues.some(
      (v) => typeof v === "string" && (/^\$/.test(v) || /\$/.test(String(v)))
    )
  ) {
    return {
      type: "money",
      format: "$",
      unit: "USD",
      description: "Auto-detected money field",
    };
  }

  // Todo Détecter l'argent EUR
  if (
    sampleValues.some(
      (v) => typeof v === "string" && (/^\$/.test(v) || /\$/.test(String(v)))
    )
  ) {
    return {
      type: "money",
      format: "$",
      unit: "EUR",
      description: "Auto-detected money field",
    };
  }

  // Détecter les aires (sqft, m², etc.)
  if (
    sampleValues.some(
      (v) =>
        typeof v === "string" &&
        /(sqft|sq\.?\s*ft|square.?feet|m²|m2)/i.test(String(v))
    )
  ) {
    return {
      type: "area",
      format: "sqft",
      unit: "square feet",
      description: "Auto-detected area field",
    };
  }

  // Détecter les pourcentages
  if (sampleValues.some((v) => typeof v === "string" && /%/.test(String(v)))) {
    return {
      type: "percentage",
      format: "%",
      description: "Auto-detected percentage field",
    };
  }

  // Détecter les emails
  if (
    sampleValues.some(
      (v) => typeof v === "string" && /@/.test(v) && /\.[a-z]{2,}$/i.test(v)
    )
  ) {
    return { type: "email", description: "Auto-detected email field" };
  }

  // Détecter les téléphones
  if (
    sampleValues.some(
      (v) =>
        typeof v === "string" &&
        /[\d\-\(\)\+\s]{10,}/.test(v.replace(/\s/g, ""))
    )
  ) {
    return { type: "phone", description: "Auto-detected phone field" };
  }

  // Détecter les nombres
  if (sampleValues.every((v) => !isNaN(Number(v)) && v !== "")) {
    return { type: "number", description: "Auto-detected number field" };
  }

  // Détecter les booléens
  if (
    sampleValues.every(
      (v) =>
        String(v).toLowerCase() === "true" ||
        String(v).toLowerCase() === "false" ||
        v === true ||
        v === false ||
        String(v).toLowerCase() === "yes" ||
        String(v).toLowerCase() === "no"
    )
  ) {
    return { type: "boolean", description: "Auto-detected boolean field" };
  }

  // Par défaut : texte
  return { type: "text", description: "Default text field" };
}

/**
 * Extrait tous les chemins de champs d'un objet de façon récursive
 */
function extractFieldPaths(
  obj: any,
  prefix = "",
  visited = new Set()
): string[] {
  if (visited.has(obj) || obj === null || obj === undefined) {
    return [];
  }

  if (typeof obj !== "object") {
    return prefix ? [prefix] : [];
  }

  visited.add(obj);
  const paths: string[] = [];

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const currentPath = prefix ? `${prefix}.${key}` : key;
      const value = obj[key];

      if (Array.isArray(value)) {
        // Si c'est un tableau, parcourir ses éléments
        if (value.length > 0) {
          // Prendre le premier élément comme échantillon pour la structure
          const firstElement = value[0];
          if (typeof firstElement === "object" && firstElement !== null) {
            // Extraire les chemins de l'objet échantillon avec notation de tableau
            const arrayPaths = extractFieldPaths(
              firstElement,
              currentPath,
              visited
            );
            paths.push(...arrayPaths);
          } else {
            // Si les éléments du tableau ne sont pas des objets, ajouter le chemin du tableau
            paths.push(currentPath);
          }
        } else {
          // Tableau vide, ajouter quand même le chemin
          paths.push(currentPath);
        }
      } else if (typeof value === "object" && value !== null) {
        paths.push(...extractFieldPaths(value, currentPath, visited));
      } else {
        paths.push(currentPath);
      }
    }
  }

  visited.delete(obj);
  return paths;
}

/**
 * Obtient la valeur d'un champ à partir de son chemin
 */
function getValueByPath(obj: any, path: string): any {
  return path.split(".").reduce((current, key) => {
    if (!current) return undefined;

    // Si la clé courante pointe vers un tableau, collecter les valeurs de tous les éléments
    if (Array.isArray(current[key])) {
      return current[key];
    }

    // Si on est dans un tableau, collecter les valeurs de tous les éléments pour cette clé
    if (Array.isArray(current)) {
      const values = current
        .map((item) =>
          item && item[key] !== undefined ? item[key] : undefined
        )
        .filter((v) => v !== undefined);
      return values.length > 0 ? values : undefined;
    }

    return current[key] !== undefined ? current[key] : undefined;
  }, obj);
}

export const useFieldConfigStore = create<FieldConfigState>()(
  persist(
    (set, get) => ({
      fieldConfigs: [],
      isInitialized: false,

      setFieldConfigs: (configs) => {
        set({ fieldConfigs: configs, isInitialized: true });
      },

      updateFieldConfig: (fieldPath, updates) => {
        set((state) => ({
          fieldConfigs: state.fieldConfigs.map((config) =>
            config.fieldPath === fieldPath ? { ...config, ...updates } : config
          ),
        }));
      },

      addFieldConfig: (config) => {
        set((state) => ({
          fieldConfigs: [...state.fieldConfigs, config],
        }));
      },

      removeFieldConfig: (fieldPath) => {
        set((state) => ({
          fieldConfigs: state.fieldConfigs.filter(
            (config) => config.fieldPath !== fieldPath
          ),
        }));
      },

      resetToDefaults: () => {
        set({ fieldConfigs: [], isInitialized: false });
      },

      analyzeAndInitializeFields: (data) => {
        if (!data || data.length === 0) {
          return;
        }

        // Extraire tous les chemins de champs uniques
        const allFieldPaths = new Set<string>();
        const fieldValues: Record<string, any[]> = {};

        data.forEach((item) => {
          const paths = extractFieldPaths(item);
          paths.forEach((path) => {
            console.log("path", path);
            allFieldPaths.add(path);
            if (!fieldValues[path]) {
              fieldValues[path] = [];
            }
            const value = getValueByPath(item, path);
            if (value !== undefined && value !== null) {
              fieldValues[path].push(value);
            }
          });
        });

        // Analyser chaque champ pour déterminer son type
        const configs: FieldConfig[] = [];
        let priority = 0;

        Array.from(allFieldPaths)
          .sort() // Trier alphabétiquement
          .forEach((fieldPath) => {
            const values = fieldValues[fieldPath] || [];
            const fieldType = analyzeFieldType(fieldPath, values);

            // Créer un nom d'affichage plus lisible
            const displayName = fieldPath
              .split(".")
              .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
              .join(" → ");

            configs.push({
              fieldPath,
              displayName,
              fieldType,
              isActive: true,
              priority: priority++,
            });
          });

        set({ fieldConfigs: configs, isInitialized: true });
      },

      exportConfiguration: () => {
        const state = get();
        const exportData = {
          version: "1.0",
          timestamp: new Date().toISOString(),
          fieldConfigs: state.fieldConfigs,
          isInitialized: state.isInitialized,
        };
        return JSON.stringify(exportData, null, 2);
      },

      importConfiguration: (configData: string) => {
        try {
          const importedData = JSON.parse(configData);

          // Validate the structure
          if (
            !importedData.fieldConfigs ||
            !Array.isArray(importedData.fieldConfigs)
          ) {
            throw new Error(
              "Invalid configuration format: fieldConfigs is missing or not an array"
            );
          }

          // Validate each field config
          for (const config of importedData.fieldConfigs) {
            if (!config.fieldPath || !config.displayName || !config.fieldType) {
              throw new Error(
                "Invalid field configuration: missing required properties"
              );
            }
            if (!config.fieldType.type) {
              throw new Error("Invalid field type: type is required");
            }
          }

          set({
            fieldConfigs: importedData.fieldConfigs,
            isInitialized:
              importedData.isInitialized !== undefined
                ? importedData.isInitialized
                : true,
          });

          return true;
        } catch (error) {
          console.error("Error importing configuration:", error);
          return false;
        }
      },
    }),
    {
      name: "field-config-storage",
      partialize: (state) => ({
        fieldConfigs: state.fieldConfigs,
        isInitialized: state.isInitialized,
      }),
    }
  )
);
